'use client';

import { useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTrackVersionPartialUpdate, getTrackRetrieveQueryKey, getTrackGetVersionsQueryKey } from '@/lib/api/orval';
import type { TrackVersion } from '@/lib/api/orval/models';
import { downloadAudio, isDownloadableUrl } from '@/lib/utils/download';
import { useToast } from './useToast';

// ============================================================================
// Types
// ============================================================================

interface UseTrackVersionEditOptions {
  trackId: string;
  version: TrackVersion;
  /** 다운로드 파일명에 사용 */
  trackTitle?: string;
}

export interface UseTrackVersionEditReturn {
  // 제목 편집
  isEditingTitle: boolean;
  editingTitle: string;
  setEditingTitle: (value: string) => void;
  handleStartEditTitle: () => void;
  handleSaveTitle: () => void;
  handleCancelEditTitle: () => void;
  handleTitleKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  isSavingTitle: boolean;

  // 다운로드
  isDownloading: boolean;
  canDownload: boolean;
  handleDownload: () => Promise<void>;

  // 재생시간 표시
  durationDisplay: string | null;

  // Extend 관련
  canExtend: boolean;
  showExtendConfirm: boolean;
  setShowExtendConfirm: (value: boolean) => void;
}

// ============================================================================
// 최대 확장 가능 시간 (8분 = 480초)
// ============================================================================
const MAX_EXTEND_DURATION_SECONDS = 480;

// ============================================================================
// Hook
// ============================================================================

/**
 * 트랙 버전의 제목 편집, 다운로드, 재생시간 표시 등 공통 로직을 제공하는 훅
 * TrackVersionCard와 TrackVersionItem에서 공유
 */
export function useTrackVersionEdit({
  trackId,
  version,
  trackTitle,
}: UseTrackVersionEditOptions): UseTrackVersionEditReturn {
  const { success: toastSuccess, error: toastError } = useToast();
  const queryClient = useQueryClient();

  // 제목 편집 상태
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editingTitle, setEditingTitle] = useState(version.title || '');

  // 다운로드 상태
  const [isDownloading, setIsDownloading] = useState(false);

  // Extend 확인 상태
  const [showExtendConfirm, setShowExtendConfirm] = useState(false);

  // 제목 수정 mutation
  const updateVersion = useTrackVersionPartialUpdate({
    mutation: {
      onSuccess: () => {
        toastSuccess('제목이 수정되었습니다');
        setIsEditingTitle(false);
        queryClient.invalidateQueries({ queryKey: getTrackGetVersionsQueryKey(trackId) });
        queryClient.invalidateQueries({ queryKey: getTrackRetrieveQueryKey(trackId) });
      },
      onError: () => {
        toastError('제목 수정에 실패했습니다');
      },
    },
  });

  // 제목 수정 시작
  const handleStartEditTitle = useCallback(() => {
    setEditingTitle(version.title || '');
    setIsEditingTitle(true);
  }, [version.title]);

  // 제목 저장
  const handleSaveTitle = useCallback(() => {
    const trimmedTitle = editingTitle.trim();
    if (trimmedTitle === (version.title || '')) {
      setIsEditingTitle(false);
      return;
    }
    updateVersion.mutate({
      id: version.id,
      data: { title: trimmedTitle },
    });
  }, [editingTitle, version.id, version.title, updateVersion]);

  // 제목 수정 취소
  const handleCancelEditTitle = useCallback(() => {
    setEditingTitle(version.title || '');
    setIsEditingTitle(false);
  }, [version.title]);

  // Enter/Escape 키 핸들러
  const handleTitleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSaveTitle();
    } else if (e.key === 'Escape') {
      handleCancelEditTitle();
    }
  }, [handleSaveTitle, handleCancelEditTitle]);

  // 다운로드 가능 여부
  const canDownload = isDownloadableUrl(version.audioUrl);

  // 다운로드 핸들러
  const handleDownload = useCallback(async () => {
    if (!version.audioUrl || isDownloading) {
      return;
    }

    setIsDownloading(true);
    try {
      const success = await downloadAudio(
        version.audioUrl,
        trackTitle,
        version.versionNumber
      );
      if (success) {
        toastSuccess('다운로드가 시작되었습니다');
      } else {
        toastError('다운로드에 실패했습니다');
      }
    } catch {
      toastError('다운로드 중 오류가 발생했습니다');
    } finally {
      setIsDownloading(false);
    }
  }, [version.audioUrl, isDownloading, trackTitle, version.versionNumber, toastSuccess, toastError]);

  // 재생시간 표시
  const durationMinutes = version.durationSeconds ? Math.floor(version.durationSeconds / 60) : 0;
  const durationSecondsRemainder = version.durationSeconds ? version.durationSeconds % 60 : 0;
  const durationDisplay = version.durationSeconds
    ? `${durationMinutes}:${durationSecondsRemainder.toString().padStart(2, '0')}`
    : null;

  // Extend 가능 여부: sunoAudioId가 있고 duration이 8분 미만일 때
  const canExtend = !!version.sunoAudioId && (version.durationSeconds || 0) < MAX_EXTEND_DURATION_SECONDS;

  return {
    isEditingTitle,
    editingTitle,
    setEditingTitle,
    handleStartEditTitle,
    handleSaveTitle,
    handleCancelEditTitle,
    handleTitleKeyDown,
    isSavingTitle: updateVersion.isPending,

    isDownloading,
    canDownload,
    handleDownload,

    durationDisplay,

    canExtend,
    showExtendConfirm,
    setShowExtendConfirm,
  };
}
